import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * Right-side overlay drawer. Rendered through a portal to <body> so it
 * floats over the page from the right edge — it never expands the header or
 * pushes the rest of the app down, and its width is independent of the
 * surrounding layout. Closes on backdrop click or Escape.
 */
export function Drawer({ open, onClose, title, children }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Move focus into the drawer for keyboard users.
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={title}>
      <div className="ms-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="ms-drawer">
        <div className="ms-drawer-header">
          <h2 className="ms-title">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ms-btn ms-btn-ghost ms-btn-xs !px-2 !py-1"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="ms-drawer-body">{children}</div>
      </aside>
    </div>,
    document.body,
  );
}
